import React from "react";
import styles from "./style.module.scss";
import ContactUsForm from "@components/shared/contactUs/contactUsForm";
import Image from "next/image";
import mvaImage from "./partner-with-us.png";
import genericImage from "./generic.png";
import { useDomainContext } from "@hooks/useDomainContext";
import { ContactUsField } from "@lib/shared/types";
import { commonPartnerWithUsAllowedCategories } from "@lib/sharedUtils";
import { genericFields } from "./genericPartnerWithUsForm";

const PartnerWithUs = () => {
    const { domain, doNotSellFormStates, domainNameTranslation } =
        useDomainContext();
    const mvaFields: ContactUsField[] = [
        {
            name: "describeYourOrganization",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsDescribeYourOrganizationLabel`
                ] ?? "How Would You Describe Your Organization?",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsPlaceholderSelectWord`
                ] ?? "Select",
            inputType: "select",
            validation: {
                required: true,
                rules: {
                    type: "isEmpty",
                },
            },
            options: [
                {
                    label:
                        domain?.translations?.[
                            `${domainNameTranslation}_partnerWithUsOptionLawFirmLabel`
                        ] ?? "Law Firm",
                    value: "Law Firm",
                    id: 2,
                },
                {
                    label:
                        domain?.translations?.[
                            `${domainNameTranslation}_partnerWithUsOptionMarketingAgencyLabel`
                        ] ?? "Marketing Agency",
                    value: "Marketing Agency",
                    id: 1,
                },
            ],
            type: null,
            isExtraField: true,
        },
        {
            name: "targetingStates",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsTargetingStatesLabel`
                ] ?? "Which State(s) Are You Targeting?",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsPlaceholderSelectWord`
                ] ?? "Select",
            inputType: "multiSelect",
            validation: {
                required: true,
                rules: {
                    type: "isEmpty",
                },
            },
            options:
                doNotSellFormStates?.map((i) => ({
                    label: i.label,
                    value: i.label,
                    id: i.id,
                })) ?? [],
            type: null,
            isExtraField: true,
        },
        {
            name: "casesHandlePerMonth",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsCasesHandlePerMonthLabel`
                ] ?? "How Many Cases Are You Willing to Handle Per Month?",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsPlaceholderSelectWord`
                ] ?? "Select",
            inputType: "select",
            validation: {
                required: true,
                rules: {
                    type: "isEmpty",
                },
            },
            options: [
                {
                    label:
                        domain?.translations?.[
                            `${domainNameTranslation}_partnerWithUsLessThan5Label`
                        ] ?? "Less than 5",
                    value: "Less Than 5",
                    id: 1,
                },
                {
                    label:
                        domain?.translations?.[
                            `${domainNameTranslation}_partnerWithUs5To10Label`
                        ] ?? "5 To 10",
                    value: "5 To 10",
                    id: 2,
                },
                {
                    label: "10+",
                    value: "10+",
                    id: 3,
                },
            ],
            type: null,
            isExtraField: true,
        },
        {
            name: "companyName",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsCompanyNameLabel`
                ] ?? "Company Name",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsCompanyNamePlaceholder`
                ] ?? "Company Name",
            inputType: "text",
            type: "text",
            validation: {
                required: true,
                rules: {
                    type: "isEmpty",
                },
            },
            isExtraField: true,
        },
        {
            name: "firstName",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsFirstNameLabel`
                ] ?? "First Name",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsFirstNamePlaceholder`
                ] ?? "First Name",
            inputType: "text",
            type: "text",
            validation: {
                required: true,
                rules: {
                    type: "isEmpty",
                },
            },
            autocomplete: "given-name",
            serverSideValidation: true,
        },
        {
            name: "lastName",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsLastNameLabel`
                ] ?? "Last Name",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsLastNamePlaceholder`
                ] ?? "Last Name",
            inputType: "text",
            type: "text",
            validation: {
                required: true,
                rules: {
                    type: "isEmpty",
                },
            },
            autocomplete: "family-name",
            serverSideValidation: true,
        },
        {
            name: "email",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsEmailLabel`
                ] ?? "Email",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsEmailPlaceholder`
                ] ?? "Email",
            inputType: "text",
            type: "email",
            validation: {
                required: true,
                rules: {
                    type: "isEmail",
                },
            },
            autocomplete: "email",
            serverSideValidation: true,
        },
        {
            name: "phone",
            label:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsPhoneLabel`
                ] ?? "Phone Number",
            placeholder:
                domain?.translations?.[
                    `${domainNameTranslation}_partnerWithUsPhonePlaceholder`
                ] ?? "Phone Number",
            inputType: "text",
            type: "tel",
            validation: {
                required: true,
                rules: {
                    type: "isPhone",
                },
            },
            autocomplete: "tel",
        },
        {
            name: "googleReCaptcha",
            label: "",
            placeholder: "",
            inputType: "recaptcha",
            type: null,
            validation: {
                required: true,
            },
        },
    ];
    const isMVA = domain.categories.every((c) =>
        commonPartnerWithUsAllowedCategories.includes(c.slug),
    );

    return (
        <div className="flex flex-col lg:flex-row lg:gap-32 mb-20 w-full items-start">
            <div className="max-w-[600px] mx-auto lg:mx-0">
                <h1 className="font-bold  lg:text-5xl text-3xl lg:leading-[3.5rem] leading-8">
                    {domain?.translations?.[
                        `${domainNameTranslation}_partnerWithUsTitle`
                    ] ?? "Partner With Us"}
                </h1>
                <p className=" lg:text-lg lg:leading-8 font-light mt-3 mb-3">
                    {domain?.translations?.[
                        `${domainNameTranslation}_partnerWithUsDescription`
                    ] ??
                        "Partner with our expert team to generate high-quality leads and increase your sales revenue. Contact us today."}
                </p>

                <div className={`${styles.form}`}>
                    <ContactUsForm
                        primaryColor={"var(--primary, #bf0a2f)"}
                        secondaryColor="#000000"
                        submitButtonTitle={
                            domain?.translations?.[
                                `${domainNameTranslation}_partnerWithUsSendButton`
                            ] ?? "Send"
                        }
                        classes={{
                            formClassName: styles["form"],
                            fieldWrapperClassName: isMVA
                                ? styles["mva-field-wrapper"]
                                : styles["generic-field-wrapper"],
                            fieldClassName: styles["field"],
                            selectFieldClassName: styles["field"],
                            fieldErrorClassName: styles["errorStyle"],
                            thankYouStepClassName: styles["thankYouStyle"],
                        }}
                        domainInfo={domain}
                        fields={isMVA ? mvaFields : genericFields}
                        type={"partnerWithUs"}
                        category={domain.categories[0].name}
                        message={
                            domain?.translations?.[
                                `${domainNameTranslation}_partnerWithUsMessage`
                            ] ??
                            "Please enter the details of your request, a member of our support staff will get back to you within 24 hours"
                        }
                    />
                </div>
            </div>
            <div
                className={`mx-auto lg:mx-0  md:w-[460px] md:min-w-[460px] md:h-[600px]  sticky top-0 `}
            >
                <Image
                    src={isMVA ? mvaImage : genericImage}
                    alt="img"
                    layout="fill"
                    objectFit="contain"
                    priority
                />
            </div>
        </div>
    );
};
export default PartnerWithUs;
